import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { getAuditLogCacheKey, onAuditLogComment, onAuditLogUpdate } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';
import { useLoadedUser } from './useUser';

const intl = getIntl('useAuditLog', {
    errorLoadingAuditLog: 'Error loading activity details',
    errorDeletingAuditLog: 'Error deleting activity details',
    deleteAuditLogSuccess: 'Message deleted',
    errorCommenting: 'Error commenting on activity',
    commentPosted: 'Comment successfully posted',
});

export type UseAuditLogArgs = {
    auditLogId: string;
    medicalProfileId: string;
    onDeleteAuditLogSuccess?: () => void;
};

export type UseAuditLogReturnValue = {
    auditLog: APITypes.auditLogService.AuditLogDetails | null;
    isLoadingAuditLog: boolean;
    deleteAuditLog: () => void;
    isDeletingAuditLog: boolean;
    comment: (comment: string) => void;
    isCommenting: boolean;
};

export const useAuditLog = (args: UseAuditLogArgs): UseAuditLogReturnValue => {
    const notify = useNotify();
    const queryClient = useQueryClient();
    const user = useLoadedUser({});

    const auditLogQuery = useQuery({
        queryKey: getAuditLogCacheKey(args.medicalProfileId, args.auditLogId),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.auditLogService.AuditLogDetailsResponse>(
                urls.api.auditLogs.auditLog(args.medicalProfileId, args.auditLogId),
            );

            return response?.auditLogDetails;
        },
    });
    useQueryErrorNotify(auditLogQuery, intl('errorLoadingAuditLog'));

    const deleteAuditLogMutation = useMutation({
        mutationFn: async () => {
            await appAPI.del(urls.api.auditLogs.auditLog(args.medicalProfileId, args.auditLogId));
        },
        onSuccess: () => {
            notify('success', intl('deleteAuditLogSuccess'));
            onAuditLogUpdate.resetCache({ medicalProfileId: args.medicalProfileId, auditLogId: args.auditLogId })();
            args.onDeleteAuditLogSuccess?.();
        },
        onError: () => {
            notify('error', intl('errorDeletingAuditLog'));
        },
    });

    const comment = useMutation({
        mutationFn: async (comment: string) => {
            const body: APITypes.auditLogService.AddAuditLogCommentRequest = {
                comment,
            };
            await appAPI.post(urls.api.auditLogs.auditLogComment(args.medicalProfileId, args.auditLogId), {
                body,
            });
        },
        onSuccess: (_data, comment) => {
            queryClient.setQueriesData(
                { queryKey: getAuditLogCacheKey(args.medicalProfileId, args.auditLogId) },
                (oldData?: APITypes.auditLogService.AuditLogDetailsResponse['auditLogDetails']) => {
                    // shouldn't happen
                    if (!oldData) return oldData as any;

                    const returnValue: APITypes.auditLogService.AuditLogDetailsResponse['auditLogDetails'] = {
                        ...oldData,
                        comments: [
                            ...oldData.comments,
                            {
                                status: 'valid',
                                userId: user.user.userId ?? '',
                                comment,
                                date: new Date().toISOString(),
                                userFirstName: user.user.firstName ?? '',
                                userLastName: user.user.lastName ?? '',
                                userPreferredName: user.user.firstName ?? null,
                                auditLogCommentId: new Date().toISOString(), // doesn't matter, just needs to be unique
                                auditLogId: args.auditLogId,
                            },
                        ],
                    };

                    return returnValue;
                },
            );

            onAuditLogComment.resetCache({ medicalProfileId: args.medicalProfileId, auditLogId: args.auditLogId })();
            notify('success', intl('commentPosted'));
            return comment;
        },
        onError: () => {
            notify('error', intl('errorCommenting'));
        },
    });

    return {
        auditLog: auditLogQuery.data ?? null,
        isLoadingAuditLog: auditLogQuery.isLoading,
        deleteAuditLog: deleteAuditLogMutation.mutate,
        isDeletingAuditLog: deleteAuditLogMutation.isPending,
        comment: comment.mutate,
        isCommenting: comment.isPending,
    };
};
